import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

interface AuthContextType {
  isAuthenticated: boolean;
  userRoles: string[];
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (accounts.length > 0) {
      setIsAuthenticated(true);
      const roles = accounts[0].idTokenClaims?.roles || [];
      setUserRoles(roles);
    } else {
      setIsAuthenticated(false);
      setUserRoles([]);
    }
  }, [accounts]);

  const login = () => {
    instance.loginRedirect();
  };

  const logout = () => {
    instance.logoutRedirect().then(() => {
      setIsAuthenticated(false);
      setUserRoles([]);
      navigate('/login');
    });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRoles, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
